// export default OrganizationLayout;
import { useEffect, useState } from "react";
import Container from "@mui/material/Container";
import Fab from "@mui/material/Fab";
import AddIcon from "@mui/icons-material/Add";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import api from '../config/api';
import LinearProgress from "@mui/material/LinearProgress";
import OrganizationCard from "./OrganizationCard";
import OrganizationModal from "./OrganizationModal";
import OrganizationPointsModal from "./OrganizationPointsModal";
import { Chip, Divider, Paper } from "@mui/material";
import useStore from "../config/store";
import TopBar from "../layouts/TopBar";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';

const OrganizationLayout = () => {
  const [organizations, setOrganizations] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [modalType, setModalType] = useState("add");
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState(0);
  const [counter, setCounter] = useState(0);

  const { user, authToken, setOrganization, setOrganizationName, setProject, setProjectName } = useStore.getState();
  const { showAlert } = useStore();
  const { t } = useTranslation();

  const navigate = useNavigate();

  useEffect(() => {
    const getOrganizations = async () => {
      try {
        const response = await api.get(
          "/organizations",
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );

        if (
          response.data.response.message ===
          "The organizations were successfully obtained"
        ) {
          const organizations = response.data.response.data;
          setOrganizations(organizations);

          if (organizations.length === 0) {
            showAlert("No se han encontrado organizaciones", "info");
          } else if (user.rol === 'user' && organizations.length === 1 && organizations[0].projects.length === 1) {
            // Si hay exactamente una organización con un proyecto, seleccionarlos automáticamente
            const organization = organizations[0];
            const project = organization.projects[0];

            // Establecer la organización seleccionada (usando el estado local)
            setOrganization(organization.uid);
            setOrganizationName(organization.name);

            // Establecer el proyecto seleccionado (usando el store global)
            setProject(project.uid);
            setProjectName(project.name);

            // Navegar a la ruta principal
            navigate('/');
          }
        } else {
          setError(true);
          showAlert("Error al cargar organizaciones", "warning");
        }
        setLoaded(true);
      } catch (err) {
        showAlert("Error al cargar organizaciones", "warning");
        console.error(err);
        setError("Error al obtener organizaciones");
      }
    };

    getOrganizations();
  }, [counter, authToken, showAlert]);

  const handleOpen = (action, position) => {
    setModalType(action);
    setSelectedPosition(position);
    setOpenModal(true);
  };
  const handleClose = () => setOpenModal(false);

  const gridWidth = (size) => {
    switch (size) {
      case "xs":
        return 3;
      case "sm":
        return 6;
      case "md":
        if (organizations?.length === 1) {
          return 12;
        } else {
          return 6;
        }
      default:
        return null;
    }
  };

  return (
    <Box>
      <TopBar />

      {!error ? (
        <Box sx={{ pt: "64px" }}>
          <Paper
            sx={{
              p: 1.5,
              pr: 3,
              backgroundColor: "#6A5AF9",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderRadius: "0px !important",
            }}
          >
            <Typography
              variant="overline"
              sx={{ color: "white", ml: 4, fontSize: "1rem" }}
            >
              {t('organizations.title')}
            </Typography>
            <Box>
              {/* <img src={corporativeLogo} alt="Logo" style={{ height: '35px' }} /> */}
            </Box>
          </Paper>

          <Divider sx={{ width: "85%", m: "2rem auto", textAlign: "center" }}>
            <Chip
              sx={{
                fontSize: "1.5rem",
                backgroundColor: "transparent",
                color: "#575757",
              }}
              label={
                user.rol === "user"
                  ? t('organizations.organizationSelectUser')
                  : t('organizations.organizationSelect')
              }
            />
          </Divider>

          {!loaded && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mt: 2,
              }}
            >
              <Typography variant="body2" sx={{ color: "#6A5AF9", mb: 1 }}>
                {t('organizations.organizationLoading')}
              </Typography>
              <LinearProgress color="inherit" sx={{ width: "80%" }} />
            </Box>
          )}

          {loaded && (
            <Container
              maxWidth="xl"
              sx={{ mt: 2, mb: 4, display: "flex", flexWrap: "wrap" }}
            >
              <Grid
                container
                spacing={{ xs: 1, md: 2 }}
                columns={{ xs: 3, sm: 6, md: 12 }}
              >
                <Grid item xs={12} sm={12} md={12}></Grid>
                {organizations?.length > 0 ? (
                  organizations?.map((organization, i) => (
                    <Grid
                      key={i}
                      item
                      xs={gridWidth("xs")}
                      sm={gridWidth("sm")}
                      md={gridWidth("md")}
                    >
                      <OrganizationCard
                        key={"o" + i}
                        name={organization.name}
                        handleOpen={handleOpen}
                        position={i}
                        organizationId={organization.uid}
                        projects={organization.projects}
                        imgPath={organization.imgPath}
                        role={user.rol}
                        plan={organization.plan}
                      />
                    </Grid>
                  ))
                ) : (
                  <Grid item xs={12} sm={12} md={12}>
                    <Typography variant="h6" component="h4">
                      {user.rol !== "super-admin"
                        ? t('organizations.organizationCreate')
                        : t('organizations.organizationCreateUser')}
                    </Typography>
                  </Grid>
                )}
              </Grid>
              {user.rol !== "super-admin" ? null : (
                <Fab
                  onClick={() => handleOpen("add")}
                  color="warning"
                  aria-label="add"
                  sx={{
                    position: "fixed",
                    bottom: 32,
                    right: 64,
                  }}
                >
                  <AddIcon />
                </Fab>
              )}
            </Container>
          )}
        </Box>
      ) : (
        <Grid item xs={12} sm={12} md={12} sx={{ mt: 3 }}>
          <Typography
            variant="h6"
            component="h4"
            sx={{
              color: "red",
              textAlign: "center",
              display: "flex",
              justifyContent: "center",
              alignItems: "center"
            }}
          >
            {t('organizations.loadError')}
          </Typography>
        </Grid>
      )}

      {openModal && modalType !== "points" && (
        <OrganizationModal
          organization={organizations[selectedPosition] ?? null}
          openModal={openModal}
          handleClose={handleClose}
          type={modalType}
          setCounter={setCounter}
          showAlert={showAlert}
          authToken={authToken}
        />
      )}

      {openModal && modalType === "points" && (
        <OrganizationPointsModal
          organization={organizations[selectedPosition] ?? null}
          openModal={openModal}
          handleClose={handleClose}
          setCounter={setCounter}
          showAlert={showAlert}
          authToken={authToken}
        />
      )}
    </Box>
  );
};

export default OrganizationLayout;
